/**
 * InnovationRadar Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * An interactive innovation radar display for the Irish Funds Innovation Centre.
 * Features include:
 * - Dynamic visualization of innovation types and drivers
 * - Interactive topic/theme exploration with detailed modals
 * - IFIA brand color-coded categorization system
 * - Responsive grid layout with hover tooltips
 * - Data-driven content from JSON configuration
 * - Advanced filtering and sorting capabilities
 * - Professional modal integrations for detailed information
 * - Production-ready with comprehensive error handling
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of innovation data objects with topics, drivers, and categories
 * @returns {JSX.Element} Innovation radar visualization component
 */
import React, { useState, useEffect } from 'react';
import ModalInnovation from '../../modals/ModalInnovation';
import InfoModal from '../../modals/InfoModal';
import './InnovationRadar.css';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import innovationData from '../../data/innovation-radar-data.json';

/**
 * InnovationRadar Component
 * 
 * Displays an interactive innovation radar with types, drivers, and topics.
 * Integrates with modal systems for detailed information display.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Innovation data array
 * @returns {JSX.Element} Innovation radar component
 */
const InnovationRadar = ({ data }) => {
    const [modalData, setModalData] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [filteredData, setFilteredData] = useState(data);
    const selectedType = useState(null);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [infoModalData, setInfoModalData] = useState(null);
    const [infoModalTitle, setInfoModalTitle] = useState('');

    const { driverColumns, driverDefinitions, typeDefinitions, typeColumns } = innovationData;

    useEffect(() => {
        // Reset to show all data initially
        setFilteredData(data);
    }, [data]);

    /**
     * Formats driver ID into human-readable title
     * 
     * @param {string} driverId - The driver identifier
     * @returns {string} Formatted driver title
     */
    const formatDriverTitle = (driverId) => {
        const driver = driverColumns.find(d => d.id === driverId);
        return driver ? driver.title : driverId;
    };

    const handleTopicClick = (topic) => {
        const topicData = filteredData.find(item => item.Topic === topic);
        if (!topicData) {
            return;
        }

        const modalTopicData = {
            title: topic,
            description: topicData.Description || 'N/A',
            primaryBenefit: topicData.PrimaryBenefit || 'N/A',
            category: topicData.Category || 'N/A',
            driver: formatDriverTitle(topicData.DriversInnovation) || 'N/A',
            topic: topicData.Topic || 'N/A'
        };
        setModalData(modalTopicData);
        setShowModal(true);
    };

    /**
     * Sorts topics by group color and ranking flag
     * 
     * @param {Array} topics - Array of topic objects to sort
     * @returns {Array} Sorted array of topics
     */
    const sortTopics = (topics) => {
        return [...topics].sort((a, b) => {
            if (a.GroupColour < b.GroupColour) return -1;
            if (a.GroupColour > b.GroupColour) return 1;
            return parseInt(a.RankingFlag) - parseInt(b.RankingFlag);
        });
    };

    /**
     * Maps driver class to IFIA brand colors
     * 
     * @param {string} driverClass - The driver class identifier
     * @returns {string} CSS color value for the driver class
     */
    function getCategoryColor(driverClass) {
        switch (driverClass) {
            case 'business': return 'var(--ifia-blue)';
            case 'market': return 'var(--ifia-green)';
            case 'maintenance': return 'var(--ifia-lightblue)';
            case 'development': return 'var(--ifia-purple)';
            case 'emerging': return 'var(--ifia-orange)';
            default: return '#000000';
        }
    }

    const handleInfoClick = (type, title, modalTitle) => {
        const definition = type === 'driver' ? driverDefinitions[title] : typeDefinitions[title];
        setInfoModalData(definition);
        setInfoModalTitle(modalTitle);
        setShowInfoModal(true);
    };

    return (
        <div className="innovation-radar">
            <div className="innovation-header">
                <h2>TYPES OF INNOVATION</h2>
                <div className="innovation-types">
                    {typeColumns.map(type => (
                        <div 
                            key={type.id}
                            onClick={() => handleInfoClick('type', type.id, type.title)} 
                            className={`dataBox type ${type.class} pointer ${selectedType === type.id ? 'active' : ''}`}
                        >
                            {type.title}
                        </div>
                    ))}
                </div>
            </div>

            <div className="drivers-section">
                <h2>DRIVERS OF INNOVATION</h2>
                <div className="drivers-container">
                    {driverColumns.map(driver => (
                        <div key={driver.id} className={`dataBox driver-item ${driver.class} clickable`}>
                            <div onClick={() => handleInfoClick('driver', driver.id, driver.title)}>
                                <Tippy content={driver.title}>
                                    <span className='driver-title'>{driver.title}</span>
                                </Tippy>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="topics-section">
                <h2>TOPICS / THEMES</h2>
                <div className="topics-container">
                    {driverColumns.map(driver => {
                        const driverTopics = sortTopics(data.filter(item =>
                            item.DriversInnovation === driver.id
                        ));
                        return driverTopics.length > 0 ? (
                            <div key={driver.id} className="topics-column">
                                {driverTopics.map(item => (
                                    <div 
                                        key={item.ID} 
                                        className={`dataBox topic-item clickable`}
                                        style={{ borderRightColor: getCategoryColor(driver.class) }}
                                        onClick={() => handleTopicClick(item.Topic)}
                                    >
                                            <Tippy content={item.Topic}>
                                                <span className='topic-title'>{item.Topic}</span>
                                            </Tippy>    
                                    </div>
                                ))}
                            </div>
                        ) : null;
                    })}
                </div>
            </div>

            <ModalInnovation
                data={modalData || {}}
                show={showModal}
                onClose={() => setShowModal(false)}
                showImage={modalData?.topic ? true : false}
                onTopicClick={handleTopicClick}
            />

            <InfoModal
                data={infoModalData}
                title={infoModalTitle}
                show={showInfoModal}
                onClose={() => setShowInfoModal(false)}
                small={true}
            />
        </div>
    );
};

export default InnovationRadar; 